'use client';
import { useMemo, useRef, useState } from 'react';
import { filterCanon } from '../lib/canon';
import type { CanonGuess } from '../lib/types';

export function DecodeGuessInput({
  canon,
  guessedIds,
  disabled,
  onGuess,
}: {
  canon: CanonGuess[];
  guessedIds: Set<string>;
  disabled: boolean;
  onGuess: (entry: CanonGuess) => void;
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(
    () => filterCanon(canon, query, guessedIds),
    [canon, query, guessedIds],
  );

  // Pin the input to the top of the visual viewport so the dropdown gets the
  // space between it and the keyboard. iOS only scrolls the focused INPUT
  // into view — the absolutely-positioned list below it ends up behind the
  // keyboard without this. Delay past the keyboard/scroll settle (~250ms on
  // iOS) or the browser's own focus scroll wins the fight.
  function scrollIntoPlace() {
    window.setTimeout(() => {
      wrapRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 300);
  }

  function commit(entry: CanonGuess) {
    onGuess(entry);
    setQuery('');
    setActive(0);
    inputRef.current?.focus();
    // The new guess row just pushed the input down a row — follow it.
    scrollIntoPlace();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!matches.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commit(matches[active].entry);
    }
  }

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        maxWidth: 520,
        margin: '0 auto',
        scrollMarginTop: 12,
      }}
    >
      <input
        ref={inputRef}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
        onFocus={scrollIntoPlace}
        placeholder="Type a make, model, or nickname…"
        aria-label="Guess a car"
        style={{
          width: '100%',
          padding: '12px 14px',
          fontSize: 16,
          borderRadius: 10,
          border: '1px solid var(--dc-rule)',
          background: 'var(--dc-card)',
          color: 'var(--dc-ink)',
        }}
      />
      {query && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 10,
            left: 0,
            right: 0,
            marginTop: 4,
            listStyle: 'none',
            padding: 4,
            background: 'var(--dc-card)',
            border: '1px solid var(--dc-rule)',
            borderRadius: 10,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {matches.length === 0 ? (
            <li style={{ padding: 12, color: 'var(--dc-ink-3)' }}>
              No match in the Decode garage — only the cars in the garage are
              guessable. Try a make, generation code, or nickname.
            </li>
          ) : (
            matches.map((m, i) => (
              <li key={m.entry.id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => commit(m.entry)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    color: 'var(--dc-ink)',
                    background:
                      i === active
                        ? 'var(--game-rule-soft)'
                        : 'transparent',
                  }}
                >
                  {m.entry.label}
                  {m.matchedAlias && (
                    <span
                      style={{ color: 'var(--dc-ink-3)', marginLeft: 8 }}
                    >
                      ·"{m.matchedAlias}"
                    </span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
